/**
 * AIDES-06 — Aides complémentaires · après la contractualisation.
 *
 * Recensement → Démarches → Demandes → Réponses → Aides reçues / Honoraires.
 *
 * ⛔ Aucune règle ici : étape courante, actions à faire, statuts, montants et
 * honoraires sont CALCULÉS PAR LE SERVEUR (`/api/aides/prestations/.../suivi`).
 * ⛔ Rien ne part d'ici : un envoi est fait hors OmniHub puis MARQUÉ envoyé.
 * Pièces : GED commune (`/api/metier/documents/{id}/download`).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2, Plus, Send, Upload } from "lucide-react";
import { API, getAuthHeaders, formatDate, formatDateTimeFR } from "./helpers";
import { Bouton, Encart, carte, champ, ouvrirDocumentGed } from "./AidesContractualisationPanel";

const ETAPES = [
  { code: "contractualisation", label: "Contractualisation" },
  { code: "recensement", label: "Recensement" },
  { code: "demarches", label: "Démarches" },
  { code: "demandes", label: "Demandes" },
  { code: "reponses", label: "Réponses" },
  { code: "aides_recues", label: "Aides reçues / honoraires" },
];

const STATUT_AIDE = {
  IDENTIFIEE: ["Identifiée", "info"],
  EN_DEMARCHE: ["En démarche", "info"],
  DEMANDE_ENVOYEE: ["Demande envoyée — en attente", "info"],
  COMPLEMENT_DEMANDE: ["Complément demandé", "error"],
  ACCORDEE: ["Accordée — fonds non reçus", "success"],
  REFUSEE: ["Refusée", "error"],
  RECUE: ["Reçue", "success"],
  ABANDONNEE: ["Abandonnée", "info"],
};

const STATUT_DEMARCHE = {
  A_FAIRE: "À faire",
  EN_PREPARATION: "En préparation",
  PRETE_A_ENVOYER: "Prête à envoyer",
  ENVOYEE: "Envoyée",
  EN_ATTENTE_REPONSE: "En attente de réponse",
  RELANCE_A_FAIRE: "Relance à faire",
  COMPLEMENT_DEMANDE: "Complément demandé",
  ACCEPTEE: "Acceptée",
  REFUSEE: "Refusée",
  ABANDONNEE: "Abandonnée",
};

const LIBELLE_TRANSITION = {
  EN_PREPARATION: "Mettre en préparation",
  PRETE_A_ENVOYER: "Marquer prête à envoyer",
  A_FAIRE: "Repasser à faire",
  EN_ATTENTE_REPONSE: "Marquer en attente",
  RELANCE_A_FAIRE: "Relance à faire",
  ABANDONNEE: "Abandonner",
};

const CANAUX = {
  courrier: "Courrier",
  email_manuel: "E-mail (envoyé hors OmniHub)",
  depot_en_ligne: "Dépôt en ligne",
  main_propre: "Remis en main propre",
  autre: "Autre",
};

const TYPES_REPONSE = {
  ACCUSE_RECEPTION: "Accusé de réception",
  COMPLEMENT_DEMANDE: "Complément demandé",
  ACCEPTATION: "Acceptation",
  REFUS: "Refus",
  FONDS_RECUS: "Fonds reçus",
  INFORMATION: "Information",
};

const FACTURATION = {
  NON_APPLICABLE: "Non applicable",
  EN_ATTENTE_FONDS: "En attente des fonds",
  A_FACTURER: "À facturer",
  FACTUREE: "Facturée",
  ECART_A_REGULARISER: "Écart à régulariser",
};

const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const cle = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const label = { color: "var(--oh-text-secondary)" };
const sousCarte = { border: "0.5px solid var(--oh-border)", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8 };
const ligne = { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" };

function Pastille({ famille = "info", children, testid, statut }) {
  return (
    <span data-testid={testid} data-statut={statut}
      style={{ background: `var(--oh-${famille}-bg)`, color: `var(--oh-${famille}-text)`,
        border: `1px solid var(--oh-${famille}-border)`, borderRadius: 8, padding: "0 8px", fontWeight: 500 }}>
      {children}
    </span>
  );
}

function Champ({ libelle, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 160 }}>
      <span style={label}>{libelle}</span>
      {children}
    </label>
  );
}

function Historique({ entrees, testid }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <div>
      <Bouton aria-expanded={ouvert} data-testid={testid} onClick={() => setOuvert((v) => !v)}>
        {ouvert ? "Masquer l'historique" : `Historique (${(entrees || []).length})`}
      </Bouton>
      {ouvert && (
        <ul style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          {[...(entrees || [])].reverse().map((h, i) => (
            <li key={i} style={label}>
              {formatDateTimeFR(h.le)} · {h.par} · {h.action}
              {h.de || h.vers ? ` (${h.de ? `${h.de} → ` : ""}${h.vers || ""})` : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Formulaires ────────────────────────────────────────────────────────────

function FormAide({ onSubmit, occupe }) {
  const vide = { organisme: "", libelle: "", dispositif: "", categorie: "", montant_potentiel: "", notes: "", pieces: "" };
  const [v, setV] = useState(vide);
  const [k, setK] = useState(cle());
  const maj = (c) => (e) => setV((x) => ({ ...x, [c]: e.target.value }));
  const envoyer = async () => {
    const ok = await onSubmit({
      organisme: v.organisme, libelle: v.libelle, dispositif: v.dispositif, categorie: v.categorie,
      montant_potentiel: v.montant_potentiel === "" ? null : Number(String(v.montant_potentiel).replace(",", ".")),
      notes: v.notes, pieces_necessaires: v.pieces.split("\n").map((s) => s.trim()).filter(Boolean), cle: k,
    });
    if (ok) { setV(vide); setK(cle()); }
  };
  return (
    <div style={sousCarte} data-testid="aides-form-aide">
      <div style={{ fontWeight: 500 }}>Ajouter une aide potentielle</div>
      <div style={ligne}>
        <Champ libelle="Organisme *"><input style={champ} value={v.organisme} onChange={maj("organisme")} placeholder="ex. CNAV, Département, mairie…" data-testid="aides-aide-organisme" /></Champ>
        <Champ libelle="Libellé *"><input style={champ} value={v.libelle} onChange={maj("libelle")} placeholder="ex. Aide habitat retraite" data-testid="aides-aide-libelle" /></Champ>
        <Champ libelle="Dispositif"><input style={champ} value={v.dispositif} onChange={maj("dispositif")} placeholder="Dispositif (même non référencé)" /></Champ>
        <Champ libelle="Catégorie"><input style={champ} value={v.categorie} onChange={maj("categorie")} placeholder="ex. caisse de retraite" /></Champ>
        <Champ libelle="Montant potentiel (€)"><input style={champ} inputMode="decimal" value={v.montant_potentiel} onChange={maj("montant_potentiel")} placeholder="si connu" data-testid="aides-aide-montant" /></Champ>
      </div>
      <div style={ligne}>
        <Champ libelle="Pièces / informations nécessaires (une par ligne)">
          <textarea style={{ ...champ, height: 64, padding: 8 }} value={v.pieces} onChange={maj("pieces")} aria-label="Pièces nécessaires" />
        </Champ>
        <Champ libelle="Notes">
          <textarea style={{ ...champ, height: 64, padding: 8 }} value={v.notes} onChange={maj("notes")} aria-label="Notes" />
        </Champ>
      </div>
      <div>
        <Bouton niveau="secondaire" data-testid="aides-ajouter-aide" disabled={occupe || !v.organisme.trim() || !v.libelle.trim()} onClick={envoyer}>
          <Plus size={14} />Ajouter l'aide
        </Bouton>
      </div>
    </div>
  );
}

function FormDemarche({ aide, onSubmit, occupe }) {
  const [v, setV] = useState({ nature: "", organisme: aide.organisme || "", echeance: "", responsable: "", commentaire: "" });
  const [k, setK] = useState(cle());
  const maj = (c) => (e) => setV((x) => ({ ...x, [c]: e.target.value }));
  return (
    <div style={sousCarte} data-testid={`aides-form-demarche-${aide.id}`}>
      <div style={ligne}>
        <Champ libelle="Nature *"><input style={champ} value={v.nature} onChange={maj("nature")} placeholder="ex. Dossier de demande" data-testid="aides-demarche-nature" /></Champ>
        <Champ libelle="Organisme destinataire"><input style={champ} value={v.organisme} onChange={maj("organisme")} placeholder="Destinataire" /></Champ>
        <Champ libelle="Échéance"><input style={champ} type="date" value={v.echeance} onChange={maj("echeance")} aria-label="Échéance" /></Champ>
        <Champ libelle="Responsable"><input style={champ} value={v.responsable} onChange={maj("responsable")} placeholder="par défaut : vous" /></Champ>
      </div>
      <Champ libelle="Commentaire"><input style={champ} value={v.commentaire} onChange={maj("commentaire")} placeholder="Commentaire" /></Champ>
      <div>
        <Bouton niveau="secondaire" data-testid={`aides-ouvrir-demarche-${aide.id}`} disabled={occupe || !v.nature.trim()}
          onClick={async () => { if (await onSubmit({ ...v, pieces_necessaires: aide.pieces_necessaires || [], cle: k })) { setK(cle()); setV((x) => ({ ...x, nature: "", commentaire: "" })); } }}>
          <Plus size={14} />Commencer la démarche
        </Bouton>
      </div>
    </div>
  );
}

function FormEnvoi({ demarche, piecesDisponibles, onSubmit, occupe }) {
  const [v, setV] = useState({ date_envoi: aujourdhui(), canal: "courrier", destinataire: demarche.organisme || "", reference: "", commentaire: "" });
  const [docs, setDocs] = useState([]);
  const [fichiers, setFichiers] = useState([]);
  const [k] = useState(cle());
  const maj = (c) => (e) => setV((x) => ({ ...x, [c]: e.target.value }));
  const envoyer = () => {
    const f = new FormData();
    Object.entries(v).forEach(([c, x]) => x && f.append(c, x));
    docs.forEach((d) => f.append("document_ids", d));
    fichiers.forEach((x) => f.append("fichiers", x));
    f.append("cle", k);
    onSubmit(f);
  };
  return (
    <div style={sousCarte} data-testid={`aides-form-envoi-${demarche.id}`}>
      <div style={{ fontWeight: 500 }}>Marquer la demande comme envoyée (envoi fait hors OmniHub)</div>
      <div style={ligne}>
        <Champ libelle="Date d'envoi *"><input style={champ} type="date" value={v.date_envoi} onChange={maj("date_envoi")} aria-label="Date d'envoi" /></Champ>
        <Champ libelle="Canal *">
          <select style={champ} value={v.canal} onChange={maj("canal")} aria-label="Canal d'envoi">
            {Object.entries(CANAUX).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
        </Champ>
        <Champ libelle="Destinataire *"><input style={champ} value={v.destinataire} onChange={maj("destinataire")} placeholder="Destinataire" /></Champ>
        <Champ libelle="Référence"><input style={champ} value={v.reference} onChange={maj("reference")} placeholder="ex. n° de recommandé" /></Champ>
      </div>
      <Champ libelle="Commentaire"><input style={champ} value={v.commentaire} onChange={maj("commentaire")} placeholder="Commentaire" /></Champ>
      {piecesDisponibles.length > 0 && (
        <div style={ligne}>
          <span style={label}>Pièces déjà en GED jointes :</span>
          {piecesDisponibles.map((p) => (
            <label key={p.document_id} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
              <input type="checkbox" checked={docs.includes(p.document_id)}
                onChange={(e) => setDocs((x) => (e.target.checked ? [...x, p.document_id] : x.filter((d) => d !== p.document_id)))} />
              {p.libelle}
            </label>
          ))}
        </div>
      )}
      <div style={ligne}>
        <input type="file" multiple accept="application/pdf,image/jpeg,image/png" aria-label="Demande envoyée (copie)"
          data-testid={`aides-envoi-fichiers-${demarche.id}`} onChange={(e) => setFichiers([...e.target.files])} />
        <Bouton niveau="primaire" data-testid={`aides-marquer-envoyee-${demarche.id}`} disabled={occupe || !v.date_envoi || !v.destinataire.trim()} onClick={envoyer}>
          <Send size={14} />Marquer envoyée
        </Bouton>
      </div>
    </div>
  );
}

function FormReponse({ demarche, onSubmit, occupe }) {
  const types = demarche.fonds_possible && !demarche.reponse_possible
    ? ["FONDS_RECUS", "INFORMATION"]
    : Object.keys(TYPES_REPONSE).filter((t) => t !== "FONDS_RECUS" || demarche.fonds_possible);
  const [v, setV] = useState({ type: types[0], date: aujourdhui(), reference: "", commentaire: "", montant_accorde: "", montant_recu: "", date_reception_fonds: aujourdhui(), motif_refus: "", complement_demande: "" });
  const [fichier, setFichier] = useState(null);
  const [k] = useState(cle());
  const maj = (c) => (e) => setV((x) => ({ ...x, [c]: e.target.value }));
  const envoyer = () => {
    const f = new FormData();
    const champs = { type: v.type, date: v.date, reference: v.reference, commentaire: v.commentaire };
    if (v.type === "ACCEPTATION") champs.montant_accorde = v.montant_accorde;
    if (v.type === "REFUS") champs.motif_refus = v.motif_refus;
    if (v.type === "COMPLEMENT_DEMANDE") champs.complement_demande = v.complement_demande;
    if (v.type === "FONDS_RECUS") { champs.montant_recu = v.montant_recu; champs.date_reception_fonds = v.date_reception_fonds; }
    Object.entries(champs).forEach(([c, x]) => x && f.append(c, x));
    if (fichier) f.append("fichier", fichier);
    f.append("cle", k);
    onSubmit(f);
  };
  return (
    <div style={sousCarte} data-testid={`aides-form-reponse-${demarche.id}`}>
      <div style={{ fontWeight: 500 }}>{demarche.fonds_possible ? "Saisir la réception des fonds" : "Saisir une réponse de l'organisme"}</div>
      <div style={ligne}>
        <Champ libelle="Type *">
          <select style={champ} value={v.type} onChange={maj("type")} aria-label="Type de réponse" data-testid={`aides-reponse-type-${demarche.id}`}>
            {types.map((t) => <option key={t} value={t}>{TYPES_REPONSE[t]}</option>)}
          </select>
        </Champ>
        <Champ libelle="Date *"><input style={champ} type="date" value={v.date} onChange={maj("date")} aria-label="Date de la réponse" /></Champ>
        {v.type === "ACCEPTATION" && (
          <Champ libelle="Montant accordé (€) *"><input style={champ} inputMode="decimal" value={v.montant_accorde} onChange={maj("montant_accorde")} placeholder="Montant" data-testid={`aides-reponse-accorde-${demarche.id}`} /></Champ>
        )}
        {v.type === "FONDS_RECUS" && (
          <>
            <Champ libelle="Montant reçu (€) *"><input style={champ} inputMode="decimal" value={v.montant_recu} onChange={maj("montant_recu")} placeholder="Montant" data-testid={`aides-reponse-recu-${demarche.id}`} /></Champ>
            <Champ libelle="Date de réception des fonds *"><input style={champ} type="date" value={v.date_reception_fonds} onChange={maj("date_reception_fonds")} aria-label="Date de réception des fonds" /></Champ>
          </>
        )}
        <Champ libelle="Référence"><input style={champ} value={v.reference} onChange={maj("reference")} placeholder="Référence du courrier" /></Champ>
      </div>
      {v.type === "REFUS" && <Champ libelle="Motif du refus *"><input style={champ} value={v.motif_refus} onChange={maj("motif_refus")} placeholder="Motif" /></Champ>}
      {v.type === "COMPLEMENT_DEMANDE" && <Champ libelle="Complément demandé *"><input style={champ} value={v.complement_demande} onChange={maj("complement_demande")} placeholder="Pièces ou informations demandées" /></Champ>}
      <Champ libelle="Commentaire"><input style={champ} value={v.commentaire} onChange={maj("commentaire")} placeholder="Commentaire" /></Champ>
      <div style={ligne}>
        <input type="file" accept="application/pdf,image/jpeg,image/png" aria-label="Document de réponse" onChange={(e) => setFichier(e.target.files[0] || null)} />
        <Bouton niveau="primaire" data-testid={`aides-enregistrer-reponse-${demarche.id}`} disabled={occupe} onClick={envoyer}>
          Enregistrer la réponse
        </Bouton>
      </div>
    </div>
  );
}

function PieceUpload({ testid, onUpload, occupe }) {
  const [fichier, setFichier] = useState(null);
  const [libelle, setLibelle] = useState("");
  return (
    <div style={ligne}>
      <input type="file" accept="application/pdf,image/jpeg,image/png" aria-label="Pièce à joindre" onChange={(e) => setFichier(e.target.files[0] || null)} />
      <input style={champ} value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Libellé de la pièce" aria-label="Libellé de la pièce" />
      <Bouton data-testid={testid} disabled={occupe || !fichier}
        onClick={async () => { const f = new FormData(); f.append("fichier", fichier); f.append("libelle", libelle); if (await onUpload(f)) { setFichier(null); setLibelle(""); } }}>
        <Upload size={14} />Joindre
      </Bouton>
    </div>
  );
}

// ─── Panneau ────────────────────────────────────────────────────────────────

export function AidesSuiviPanel({ dossierId, tenantId, rafraichir }) {
  const [s, setS] = useState(undefined);
  const [ged, setGed] = useState([]);
  const [occupe, setOccupe] = useState("");
  const [ouvert, setOuvert] = useState({});
  const base = `${API}/api/aides/prestations/dossier/${dossierId}`;

  const appeler = useCallback(async (url, { method = "GET", json, form } = {}) => {
    const res = await fetch(url, {
      method, credentials: "include",
      headers: getAuthHeaders(tenantId, json ? "application/json" : null),
      body: json ? JSON.stringify(json) : form,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const d = data.detail;
      throw new Error((d && (d.message || (typeof d === "string" ? d : null))) || `Erreur ${res.status}`);
    }
    return data;
  }, [tenantId]);

  const charger = useCallback(async () => {
    try {
      const data = await appeler(`${base}/suivi`);
      setS(data);
      if (data.prestation !== null) setGed((await appeler(`${base}/ged`)).documents || []);
    } catch (e) {
      setS(null);
    }
  }, [appeler, base]);

  useEffect(() => { charger(); }, [charger, rafraichir]);

  const agir = async (cleAction, url, options, succes) => {
    setOccupe(cleAction);
    try {
      await appeler(url, { method: "POST", ...options });
      if (succes) toast.success(succes);
      await charger();
      return true;
    } catch (e) {
      toast.error(e.message);
      return false;
    } finally {
      setOccupe("");
    }
  };
  const basculer = (k) => setOuvert((x) => ({ ...x, [k]: !x[k] }));
  const nomsGed = useMemo(() => Object.fromEntries(ged.map((d) => [d.id, d.nom || d.fichier_nom])), [ged]);

  if (s === undefined) return <div style={carte}><Loader2 className="animate-spin" size={16} aria-label="Chargement" /></div>;
  if (!s || s.prestation === null) return null;
  // Avant la porte, la contractualisation suffit à l'écran.
  if (s.etape_courante === "contractualisation") return null;

  const iEtape = ETAPES.findIndex((e) => e.code === s.etape_courante);
  const c = s.compteurs || {};
  const m = s.montants || {};
  const occ = !!occupe;
  const peutRecenser = ["RECENSEMENT_EN_COURS", "RECENSEMENT_TERMINE"].includes(s.statut);
  const termine = s.statut === "RECENSEMENT_TERMINE";

  return (
    <div style={{ ...carte, display: "flex", flexDirection: "column", gap: 16 }} data-testid="aides-suivi" data-etape={s.etape_courante}>
      <div style={{ fontWeight: 500 }}>Aides complémentaires — suivi</div>

      {/* ── Étapes ── */}
      <ol data-testid="aides-etapes" style={{ display: "flex", gap: 8, flexWrap: "wrap", listStyle: "none", padding: 0, margin: 0 }}>
        {ETAPES.map((e, i) => {
          const courante = i === iEtape;
          const faite = i < iEtape;
          return (
            <li key={e.code} data-testid={`aides-etape-${e.code}`} data-courante={courante ? "1" : "0"}
              style={{
                borderRadius: 8, padding: "4px 12px", fontWeight: courante ? 500 : 400,
                background: courante ? "var(--oh-accent-bg)" : faite ? "var(--oh-success-bg)" : "transparent",
                color: courante ? "var(--oh-accent-text)" : faite ? "var(--oh-success-text)" : "var(--oh-text-muted)",
                border: `1px solid ${courante ? "var(--oh-accent)" : faite ? "var(--oh-success-border)" : "var(--oh-border)"}`,
              }}>
              {i + 1}. {e.label}
            </li>
          );
        })}
      </ol>

      {/* ── Chiffres ── */}
      <div data-testid="aides-compteurs" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
        {[
          ["Aides recensées", c.aides_recensees, "aides-c-recensees"],
          ["Démarches ouvertes", c.demarches_ouvertes, "aides-c-demarches"],
          ["Demandes en attente", c.demandes_en_attente, "aides-c-attente"],
          ["Relances", c.relances, "aides-c-relances"],
          ["Réponses", c.reponses, "aides-c-reponses"],
          ["Montant potentiel", eur(m.potentiel), "aides-m-potentiel"],
          ["Montant accordé", eur(m.accorde), "aides-m-accorde"],
          ["Montant reçu", eur(m.recu), "aides-m-recu"],
          ["Honoraires de succès TTC", eur(m.honoraires_ttc), "aides-m-honoraires"],
        ].map(([l, v, t]) => (
          <div key={t} style={sousCarte}>
            <span style={label}>{l}</span>
            <span data-testid={t} style={{ fontWeight: 500 }}>{v}</span>
          </div>
        ))}
      </div>

      {/* ── À faire ── */}
      {(s.actions || []).length > 0 && (
        <Encart famille="info" testid="aides-actions">
          <div style={{ fontWeight: 500 }}>À faire</div>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {s.actions.map((a, i) => <li key={i}>{a.libelle}</li>)}
          </ul>
        </Encart>
      )}

      {/* ── Recensement ── */}
      {s.statut === "RECENSEMENT_A_DEMARRER" && (
        <div>
          <Bouton niveau="primaire" data-testid="aides-demarrer-recensement" disabled={occ}
            onClick={() => agir("rec", `${base}/transition`, { json: { vers: "RECENSEMENT_EN_COURS" } }, "Recensement démarré")}>
            Démarrer le recensement
          </Bouton>
        </div>
      )}
      {peutRecenser && (ouvert.formAide || !termine) && (
        <FormAide occupe={occ} onSubmit={(json) => agir("aide", `${base}/aides`, { json }, "Aide recensée")} />
      )}
      {s.statut === "RECENSEMENT_EN_COURS" && (
        <div style={ligne}>
          <Bouton niveau="primaire" data-testid="aides-terminer-recensement" disabled={occ || c.aides_recensees === 0}
            onClick={() => agir("fin", `${base}/transition`, { json: { vers: "RECENSEMENT_TERMINE" } }, "Recensement terminé")}>
            Terminer le recensement
          </Bouton>
          {c.aides_recensees === 0 && (
            <Bouton data-testid="aides-constat-aucune-aide" disabled={occ}
              onClick={() => window.confirm("Constater qu'aucune aide complémentaire n'a été identifiée ?") &&
                agir("fin", `${base}/transition`, { json: { vers: "RECENSEMENT_TERMINE", constat_aucune_aide: true } }, "Recensement terminé")}>
              Aucune aide identifiée
            </Bouton>
          )}
        </div>
      )}
      {termine && (
        <div><Bouton data-testid="aides-afficher-form-aide" onClick={() => basculer("formAide")}>
          <Plus size={14} />{ouvert.formAide ? "Fermer" : "Ajouter une aide"}
        </Bouton></div>
      )}

      {/* ── Aides et démarches ── */}
      <section data-testid="aides-liste" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {(s.aides || []).map((a) => {
          const [lib, fam] = STATUT_AIDE[a.statut] || [a.statut, "info"];
          const h = a.honoraires || {};
          const pieces = [...(a.pieces || []), ...a.demarches.flatMap((d) => d.pieces || [])];
          return (
            <article key={a.id} data-testid={`aides-aide-${a.id}`} style={{ ...sousCarte, gap: 12 }}>
              <div style={{ ...ligne, justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{a.libelle}</div>
                  <div style={label}>{a.organisme}{a.dispositif ? ` · ${a.dispositif}` : ""}{a.categorie ? ` · ${a.categorie}` : ""}</div>
                </div>
                <Pastille famille={fam} testid={`aides-aide-statut-${a.id}`} statut={a.statut}>{lib}</Pastille>
              </div>
              <div style={ligne}>
                <span style={label}>Potentiel</span><span>{a.montant_potentiel != null ? eur(a.montant_potentiel) : "—"}</span>
                <span style={label}>Accordé</span><span>{eur(a.montant_accorde)}</span>
                <span style={label}>Reçu</span><span data-testid={`aides-aide-recu-${a.id}`}>{eur(a.montant_recu)}</span>
              </div>
              {(a.pieces_necessaires || []).length > 0 && <div style={label}>Pièces nécessaires : {a.pieces_necessaires.join(", ")}</div>}
              {a.notes && <div style={label}>{a.notes}</div>}

              {/* Honoraires */}
              <div data-testid={`aides-honoraires-${a.id}`} data-eligible={h.eligible ? "1" : "0"}
                style={{ ...ligne, background: "var(--oh-bg-surface)", borderRadius: 8, padding: 8 }}>
                <span style={{ fontWeight: 500 }}>Honoraires de succès</span>
                <span>{h.eligible ? "Éligible" : "Non éligible"}</span>
                <span style={label}>{h.motif}</span>
                <span style={label}>Assiette</span><span>{eur(h.assiette)}</span>
                <span style={label}>Taux</span><span>{Math.round((h.taux_ttc || 0) * 10000) / 100} % TTC</span>
                <span style={label}>Honoraires</span><span data-testid={`aides-honoraires-ttc-${a.id}`} style={{ fontWeight: 500 }}>{eur(h.honoraires_ttc)} TTC</span>
                <Pastille famille={h.statut_facturation === "ECART_A_REGULARISER" ? "error" : h.statut_facturation === "FACTUREE" ? "success" : "info"}
                  testid={`aides-facturation-${a.id}`} statut={h.statut_facturation}>
                  {FACTURATION[h.statut_facturation] || h.statut_facturation}
                </Pastille>
                {h.facture && <span style={label}>Facture {h.facture.reference} ({eur(h.facture.montant_ttc)})</span>}
                {h.statut_facturation === "A_FACTURER" && (
                  <Bouton niveau="secondaire" data-testid={`aides-tracer-facture-${a.id}`} disabled={occ}
                    onClick={() => { const r = window.prompt("Référence de la facture d'honoraires (émise hors de ce module) :"); if (r) agir("fact", `${base}/aides/${a.id}/honoraires/facture`, { json: { reference_facture: r } }, "Facture tracée"); }}>
                    Tracer la facture
                  </Bouton>
                )}
              </div>
              {a.exclusion && !a.derogation_accord_ecrit && (
                <div style={ligne}>
                  <Encart famille="info">Exclue de l'assiette ({a.exclusion}) sauf accord écrit.</Encart>
                  <Bouton disabled={occ} data-testid={`aides-derogation-${a.id}`}
                    onClick={() => { const r = window.prompt("Référence de l'accord écrit du client :"); if (r) { const f = new FormData(); f.append("reference", r); agir("derog", `${base}/aides/${a.id}/derogation`, { form: f }, "Accord écrit enregistré"); } }}>
                    Enregistrer un accord écrit
                  </Bouton>
                </div>
              )}

              {/* Pièces */}
              {pieces.length > 0 && (
                <div style={ligne}>
                  <span style={label}>Pièces :</span>
                  {pieces.map((p) => (
                    <Bouton key={p.document_id} aria-label={`Voir ${p.libelle}`} onClick={() => ouvrirDocumentGed(tenantId, p.document_id, "voir")}>
                      <Eye size={14} />{p.libelle}
                    </Bouton>
                  ))}
                </div>
              )}
              {peutRecenser && a.statut !== "ABANDONNEE" && (
                <PieceUpload testid={`aides-piece-aide-${a.id}`} occupe={occ}
                  onUpload={(form) => agir("piece", `${base}/aides/${a.id}/pieces`, { form }, "Pièce jointe")} />
              )}

              {/* Démarches */}
              {a.demarches.map((d) => (
                <div key={d.id} data-testid={`aides-demarche-${d.id}`} data-statut={d.statut} style={{ ...sousCarte, marginLeft: 16 }}>
                  <div style={{ ...ligne, justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{d.nature}</div>
                      <div style={label}>
                        {d.organisme} · début {formatDate(d.date_debut)}
                        {d.echeance ? ` · échéance ${formatDate(d.echeance)}` : ""} · {d.responsable}
                      </div>
                    </div>
                    <Pastille famille={["ACCEPTEE"].includes(d.statut) ? "success" : ["REFUSEE", "COMPLEMENT_DEMANDE", "RELANCE_A_FAIRE"].includes(d.statut) || d.echeance_depassee ? "error" : "info"}
                      testid={`aides-demarche-statut-${d.id}`} statut={d.statut}>
                      {STATUT_DEMARCHE[d.statut] || d.statut}{d.echeance_depassee ? " · échéance dépassée" : ""}
                    </Pastille>
                  </div>
                  {d.commentaire && <div style={label}>{d.commentaire}</div>}
                  {(d.pieces_necessaires || []).length > 0 && <div style={label}>Pièces nécessaires : {d.pieces_necessaires.join(", ")}</div>}
                  {(d.envois || []).map((e) => (
                    <div key={e.id} style={label} data-testid={`aides-envoi-${e.id}`}>
                      Envoi n°{e.numero} le {formatDate(e.date_envoi)} · {CANAUX[e.canal] || e.canal} · à {e.destinataire}
                      {e.reference ? ` · réf. ${e.reference}` : ""} · par {e.par}
                      {(e.documents || []).length > 0 && ` · ${e.documents.map((x) => nomsGed[x] || "pièce").join(", ")}`}
                    </div>
                  ))}
                  {(d.reponses || []).map((r) => (
                    <div key={r.id} style={{ ...ligne, ...label }} data-testid={`aides-reponse-${r.id}`}>
                      <span>
                        {formatDate(r.date)} · {TYPES_REPONSE[r.type] || r.type}
                        {r.montant_accorde != null ? ` · accordé ${eur(r.montant_accorde)}` : ""}
                        {r.montant_recu != null ? ` · reçu ${eur(r.montant_recu)} le ${formatDate(r.date_reception_fonds)}` : ""}
                        {r.motif_refus ? ` · motif : ${r.motif_refus}` : ""}
                        {r.complement_demande ? ` · ${r.complement_demande}` : ""}
                        {r.corrige_reponse_id ? " · correction" : ""}
                        {r.commentaire ? ` · ${r.commentaire}` : ""} · par {r.par}
                      </span>
                      {r.document_id && (
                        <Bouton aria-label="Voir le document de réponse" onClick={() => ouvrirDocumentGed(tenantId, r.document_id, "voir")}><Eye size={14} />Document</Bouton>
                      )}
                    </div>
                  ))}
                  <div style={ligne}>
                    {(d.transitions_possibles || []).map((t) => (
                      <Bouton key={t} data-testid={`aides-demarche-${d.id}-vers-${t}`} disabled={occ}
                        onClick={() => (t !== "ABANDONNEE" || window.confirm("Abandonner cette démarche ?")) &&
                          agir(`t-${d.id}`, `${base}/demarches/${d.id}/transition`, { json: { vers: t } })}>
                        {LIBELLE_TRANSITION[t] || t}
                      </Bouton>
                    ))}
                    {d.envoi_possible && (
                      <Bouton niveau="secondaire" data-testid={`aides-afficher-envoi-${d.id}`} onClick={() => basculer(`envoi-${d.id}`)}>
                        <Send size={14} />Envoi
                      </Bouton>
                    )}
                    {(d.reponse_possible || d.fonds_possible) && (
                      <Bouton niveau="secondaire" data-testid={`aides-afficher-reponse-${d.id}`} onClick={() => basculer(`rep-${d.id}`)}>
                        {d.fonds_possible ? "Fonds reçus" : "Réponse"}
                      </Bouton>
                    )}
                    {!["ACCEPTEE", "REFUSEE", "ABANDONNEE"].includes(d.statut) && (
                      <Bouton data-testid={`aides-afficher-piece-${d.id}`} onClick={() => basculer(`piece-${d.id}`)}><Upload size={14} />Pièce</Bouton>
                    )}
                  </div>
                  {ouvert[`piece-${d.id}`] && (
                    <PieceUpload testid={`aides-piece-demarche-${d.id}`} occupe={occ}
                      onUpload={(form) => agir("piece", `${base}/demarches/${d.id}/pieces`, { form }, "Pièce jointe")} />
                  )}
                  {d.envoi_possible && ouvert[`envoi-${d.id}`] && (
                    <FormEnvoi demarche={d} occupe={occ} piecesDisponibles={[...(a.pieces || []), ...(d.pieces || [])]}
                      onSubmit={async (form) => { if (await agir(`env-${d.id}`, `${base}/demarches/${d.id}/envoi`, { form }, "Envoi enregistré")) basculer(`envoi-${d.id}`); }} />
                  )}
                  {(d.reponse_possible || d.fonds_possible) && ouvert[`rep-${d.id}`] && (
                    <FormReponse demarche={d} occupe={occ}
                      onSubmit={async (form) => { if (await agir(`rep-${d.id}`, `${base}/demarches/${d.id}/reponses`, { form }, "Réponse enregistrée")) basculer(`rep-${d.id}`); }} />
                  )}
                  <Historique entrees={d.historique} testid={`aides-demarche-historique-${d.id}`} />
                </div>
              ))}

              <div style={ligne}>
                {termine && a.statut !== "ABANDONNEE" && (
                  <Bouton niveau="secondaire" data-testid={`aides-nouvelle-demarche-${a.id}`} onClick={() => basculer(`dem-${a.id}`)}>
                    <Plus size={14} />Nouvelle démarche
                  </Bouton>
                )}
                {!["ABANDONNEE", "RECUE"].includes(a.statut) && (
                  <Bouton data-testid={`aides-abandonner-${a.id}`} disabled={occ}
                    onClick={() => { const motif = window.prompt("Motif de l'abandon de cette aide :"); if (motif) agir("ab", `${base}/aides/${a.id}/abandonner`, { json: { motif } }, "Aide abandonnée"); }}>
                    Abandonner l'aide
                  </Bouton>
                )}
                <Historique entrees={a.historique} testid={`aides-aide-historique-${a.id}`} />
              </div>
              {termine && ouvert[`dem-${a.id}`] && (
                <FormDemarche aide={a} occupe={occ}
                  onSubmit={(json) => agir(`dem-${a.id}`, `${base}/aides/${a.id}/demarches`, { json }, "Démarche commencée")} />
              )}
            </article>
          );
        })}
        {(s.aides || []).length === 0 && peutRecenser && <div style={label}>Aucune aide recensée pour l'instant.</div>}
      </section>

      {/* ── GED du parcours ── */}
      <section data-testid="aides-ged">
        <Bouton aria-expanded={!!ouvert.ged} data-testid="aides-ged-toggle" onClick={() => basculer("ged")}>
          {ouvert.ged ? "Masquer les documents" : `Documents du parcours (${ged.length})`}
        </Bouton>
        {ouvert.ged && (
          <ul style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4, padding: 0, listStyle: "none" }}>
            {ged.map((g) => (
              <li key={g.id} style={ligne} data-testid={`aides-ged-${g.id}`}>
                <span>{g.nom || g.fichier_nom}</span>
                <span style={label}>{g.type_document} · {formatDateTimeFR(g.created_at)} · {g.archive_par || "—"}{g.version > 1 ? ` · v${g.version}` : ""}</span>
                <Bouton aria-label={`Voir ${g.nom}`} onClick={() => ouvrirDocumentGed(tenantId, g.id, "voir")}><Eye size={14} />Voir</Bouton>
                <Bouton aria-label={`Télécharger ${g.nom}`} onClick={() => ouvrirDocumentGed(tenantId, g.id, "telecharger", g.fichier_nom)}>Télécharger</Bouton>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
